import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress, Alert,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Button, Stack, Pagination, FormControl, InputLabel,
  Select, MenuItem, TextField
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { format } from 'date-fns';
import api from '../../services/api';
import { t } from '../../utils/textUtils';

const PAGE_SIZE = 10;
const EMPTY_FILTERS = { projectId: '', modularUnit: '', workCategory: '', task: '', search: '' };

const fmtDate = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd MMM yyyy');
  } catch {
    return '-';
  }
};

/**
 * Lists progress submissions that have already been APPROVED, so an approver
 * can re-check them and — if approved by mistake — revert the submission back
 * to draft (undoing its effect on task/project progress) or delete it.
 */
const ApprovedProgressList = ({ projects = [] }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });

  // { open, mode: 'revert' | 'delete', submission }
  const [confirm, setConfirm] = useState({ open: false, mode: null, submission: null });
  const [acting, setActing] = useState(false);
  const [detail, setDetail] = useState({ open: false, loading: false, data: null });

  const notify = (message, severity = 'success') =>
    setFeedback({ open: true, message, severity });

  const fetchApproved = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: 'approved', page, limit: PAGE_SIZE };
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value.trim()) params[key] = value.trim();
      });
      const res = await api.get('/progress-submissions', { params });
      setRows(res.data?.progressSubmissions || []);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    } catch (err) {
      notify(err.response?.data?.message || t('GAGAL MEMUAT DATA'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, appliedFilters]);

  useEffect(() => {
    fetchApproved();
  }, [fetchApproved]);

  const updateFilter = (name) => (event) => {
    setFilters((current) => ({ ...current, [name]: String(event.target.value) }));
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const openDetail = async (submissionId) => {
    setDetail({ open: true, loading: true, data: null });
    try {
      const res = await api.get(`/progress-submissions/${submissionId}`);
      setDetail({ open: true, loading: false, data: res.data?.progressSubmission || res.data });
    } catch (err) {
      setDetail({ open: false, loading: false, data: null });
      notify(err.response?.data?.message || t('GAGAL MEMUAT DETAIL'), 'error');
    }
  };

  const runAction = async () => {
    const { mode, submission } = confirm;
    if (!submission) return;
    setActing(true);
    try {
      if (mode === 'revert') {
        await api.post(`/progress-submissions/${submission.id}/revert`);
        notify(t('APPROVAL DIBATALKAN — LAPORAN DIKEMBALIKAN KE DRAFT'));
      } else {
        await api.delete(`/progress-submissions/${submission.id}`);
        notify(t('LAPORAN PROGRES BERHASIL DIHAPUS'));
      }
      setConfirm({ open: false, mode: null, submission: null });
      // Stay on a valid page if the last row of the page was removed.
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchApproved();
    } catch (err) {
      notify(err.response?.data?.message || t('AKSI GAGAL'), 'error');
    } finally {
      setActing(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(15, 23, 42, 0.1)',
        bgcolor: '#FFFFFF'
      }}
    >
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {t('LAPORAN PROGRES YANG SUDAH DI-APPROVE')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>{t('FILTER PROYEK')}</InputLabel>
            <Select value={filters.projectId} label={t('FILTER PROYEK')} onChange={updateFilter('projectId')}>
              <MenuItem value="">{t('SEMUA PROYEK')}</MenuItem>
              {projects.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label={t('BANGUNAN')} value={filters.modularUnit} onChange={updateFilter('modularUnit')} />
          <TextField size="small" label={t('KATEGORI')} value={filters.workCategory} onChange={updateFilter('workCategory')} />
          <TextField size="small" label={t('TASK / PEKERJAAN')} value={filters.task} onChange={updateFilter('task')} />
          <TextField
            size="small"
            label={t('CARI DATA')}
            placeholder={t('Proyek, task, bangunan, pelapor...')}
            value={filters.search}
            onChange={updateFilter('search')}
            onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" startIcon={<SearchIcon />} onClick={applyFilters}>{t('TERAPKAN')}</Button>
            <Button startIcon={<RestartAltIcon />} onClick={resetFilters}>{t('RESET')}</Button>
          </Stack>
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('Periksa kembali laporan yang sudah disetujui. Jika ada yang salah approve, kembalikan ke draft atau hapus.')}
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#3B82F6' }} />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">{t('BELUM ADA LAPORAN PROGRES YANG DI-APPROVE.')}</Alert>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{t('PROYEK')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('PEKERJAAN')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('BANGUNAN / KATEGORI')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('SUBCON')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">{t('VOLUME')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('PELAPOR')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('DISETUJUI OLEH')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('TGL APPROVE')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">{t('AKSI')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.projectName || '-'}</TableCell>
                    <TableCell>{row.taskName || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.modularUnitName || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.workCategoryName || '-'}</Typography>
                    </TableCell>
                    <TableCell>{row.subconName || '-'}</TableCell>
                    <TableCell align="right">
                      {row.volume} {row.taskUnit || ''}
                    </TableCell>
                    <TableCell>{row.submitterName || row.submitterUsername || '-'}</TableCell>
                    <TableCell>{row.reviewerName || row.reviewerUsername || '-'}</TableCell>
                    <TableCell>{fmtDate(row.reviewedAt)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={t('LIHAT DETAIL')}>
                          <IconButton size="small" color="primary" onClick={() => openDetail(row.id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('KEMBALIKAN KE DRAFT')}>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => setConfirm({ open: true, mode: 'revert', submission: row })}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('HAPUS LAPORAN')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setConfirm({ open: true, mode: 'delete', submission: row })}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Confirm revert / delete */}
      <Dialog open={confirm.open} onClose={() => !acting && setConfirm({ open: false, mode: null, submission: null })}>
        <DialogTitle>
          {confirm.mode === 'revert' ? t('KEMBALIKAN KE DRAFT?') : t('HAPUS LAPORAN PROGRES?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm.mode === 'revert'
              ? t('Approval akan dibatalkan dan laporan dikembalikan ke status draft. Progres pekerjaan & proyek akan dihitung ulang tanpa volume laporan ini.')
              : t('Laporan progres akan dihapus permanen. Jika sudah di-approve, progres pekerjaan & proyek akan dihitung ulang. Tindakan ini tidak dapat dibatalkan.')}
          </DialogContentText>
          {confirm.submission && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F8FAFC', borderRadius: 1 }}>
              <Typography variant="body2"><strong>{t('PROYEK')}:</strong> {confirm.submission.projectName}</Typography>
              <Typography variant="body2"><strong>{t('PEKERJAAN')}:</strong> {confirm.submission.taskName}</Typography>
              <Typography variant="body2"><strong>{t('VOLUME')}:</strong> {confirm.submission.volume} {confirm.submission.taskUnit || ''}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={acting} onClick={() => setConfirm({ open: false, mode: null, submission: null })}>
            {t('BATAL')}
          </Button>
          <Button
            variant="contained"
            color={confirm.mode === 'revert' ? 'warning' : 'error'}
            disabled={acting}
            onClick={runAction}
            startIcon={acting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {confirm.mode === 'revert' ? t('KEMBALIKAN KE DRAFT') : t('HAPUS')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail */}
      <Dialog open={detail.open} onClose={() => setDetail({ open: false, loading: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('DETAIL LAPORAN PROGRES')}</DialogTitle>
        <DialogContent dividers>
          {detail.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : detail.data ? (
            <Stack spacing={1}>
              <Typography variant="body2"><strong>{t('PROYEK')}:</strong> {detail.data.project?.name || detail.data.projectName || '-'}</Typography>
              <Typography variant="body2"><strong>{t('PEKERJAAN')}:</strong> {detail.data.task?.name || detail.data.taskName || '-'}</Typography>
              <Typography variant="body2"><strong>{t('VOLUME')}:</strong> {detail.data.volume} {detail.data.task?.unit || detail.data.taskUnit || ''}</Typography>
              <Typography variant="body2"><strong>{t('KETERANGAN')}:</strong> {detail.data.notes || '-'}</Typography>
              <Typography variant="body2"><strong>{t('TGL PENGERJAAN')}:</strong> {fmtDate(detail.data.progressWorkDate)}</Typography>
              <Typography variant="body2">
                <strong>{t('STATUS')}:</strong>{' '}
                <Chip size="small" label={detail.data.status} color="success" />
              </Typography>
              {Array.isArray(detail.data.attachments) && detail.data.attachments.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, mt: 1 }}>{t('LAMPIRAN')}:</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                    {detail.data.attachments.map((att) => (
                      <a key={att.id} href={att.fileUrl} target="_blank" rel="noreferrer">
                        <img
                          src={att.fileUrl}
                          alt={att.fileName || 'attachment'}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }}
                        />
                      </a>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          ) : (
            <Alert severity="warning">{t('DETAIL TIDAK TERSEDIA')}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail({ open: false, loading: false, data: null })}>{t('TUTUP')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback.severity} onClose={() => setFeedback((f) => ({ ...f, open: false }))}>
          {feedback.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ApprovedProgressList;
